// "3 minutes ago", "about a week ago": how long ago something happened, in plain words, kept current while the page
// is open (one ticker for every timestamp on the page), with the exact date and time on hover.
import * as React from 'react';

const MIN = 60_000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

const plural = (n: number, unit: string) => `${n} ${unit}${n === 1 ? '' : 's'} ago`;

/** How long before `now` the moment `iso` was, in words. */
export function timeAgo(iso: string | Date, now: number = Date.now()): string {
  const t = typeof iso === 'string' ? new Date(iso).getTime() : iso.getTime();
  if (!Number.isFinite(t)) return '—';
  const ms = now - t;
  if (ms < 45_000) return 'just now';
  if (ms < 90_000) return 'a minute ago';
  if (ms < 45 * MIN) return plural(Math.round(ms / MIN), 'minute');
  if (ms < 90 * MIN) return 'about an hour ago';
  if (ms < 22 * HOUR) return plural(Math.round(ms / HOUR), 'hour');
  // The day before on the viewer's calendar reads as "yesterday", whatever the hour.
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  if (t >= startOfToday.getTime() - DAY && t < startOfToday.getTime()) return 'yesterday';
  const days = Math.max(1, Math.round(ms / DAY));
  if (days < 7) return plural(days, 'day');
  if (days < 11) return 'about a week ago';
  if (days < 26) return plural(Math.round(days / 7), 'week');
  if (days < 45) return 'about a month ago';
  if (days < 320) return plural(Math.round(days / 30.44), 'month');
  if (days < 548) return 'about a year ago';
  return plural(Math.round(days / 365.25), 'year');
}

/** "Thursday, 8 October 2026 at 16:00": the exact moment, in the viewer's zone. */
export const exactTime = (iso: string) =>
  new Date(iso).toLocaleString([], { dateStyle: 'full', timeStyle: 'short' });

// One ticker for the whole page: every <TimeAgo> on it re-reads the clock together, every 20 seconds.
const listeners = new Set<() => void>();
let tick = Date.now();
let timer: ReturnType<typeof setInterval> | null = null;
function subscribe(fn: () => void) {
  listeners.add(fn);
  timer ??= setInterval(() => {
    tick = Date.now();
    listeners.forEach((l) => l());
  }, 20_000);
  return () => {
    listeners.delete(fn);
    if (!listeners.size && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}
const readTick = () => tick;

/** `iso` as "3 minutes ago", kept current; the exact date and time on hover. */
export function TimeAgo({ iso, className, testId }: { iso: string; className?: string; testId?: string }) {
  React.useSyncExternalStore(subscribe, readTick, readTick);
  return (
    <time dateTime={iso} title={exactTime(iso)} className={className} data-testid={testId}>
      {timeAgo(iso)}
    </time>
  );
}
